/* eslint-disable operator-linebreak */
import Image from 'common/components/Image';
import { AppSyncFarm } from 'common/models/farm';
import { Nullable, Optional } from 'common/models/optional';
import { FarmUserInfo } from 'common/models/token';
import React from 'react';
import { animated, useSpring } from 'react-spring';
import { FarmUtils } from 'utils/farmUtils';
import FormatNumber from '../FormatPercent/FormatNumber';
import SpinnerContainer from '../SpinnerContainer';
import { ExternalLinkButton } from '../LinkButton/ExternalLinkButton';
import { NumberUtils } from 'utils/numberUtils';
import VaultTooltip from './VaultTooltip';
import { ChainId } from 'constants/constants';

type Props = {
  farm: Nullable<AppSyncFarm>;
  farmUserInfo: Optional<FarmUserInfo>;
  type?: Optional<string>;
  refetchUserInfo: () => Promise<void>;
  updatePool: (poolId: number) => Promise<void>;
  loadingUserInfo: boolean;
  onDepositOpen: () => void;
  onWithdrawOpen: () => void;
  chainId: ChainId;
};

const Manage: React.FC<Props> = (props) => {
  const depositToken = FarmUtils.getBaseToken(props.farm);
  const chainId = props.chainId;

  const fadeInProps = useSpring({ opacity: !props.farm ? 0 : 1 });

  const baseAppSyncToken = FarmUtils.getBaseToken(props.farm);
  const memberAppSyncToken = FarmUtils.getRewardToken(props.farm);

  const isHodlVault = FarmUtils.isHodlVault(props.farm);

  const baseToken =
    props.farmUserInfo &&
    props.farmUserInfo.tokens.filter(
      (t) => t.name === baseAppSyncToken?.name
    )[0];
  const rewardToken =
    props.farmUserInfo &&
    props.farmUserInfo.tokens.filter(
      (t) => t.name === memberAppSyncToken?.name
    )[0];

  const baseTokenIcon = FarmUtils.tokenIconSrc(baseToken?.name);
  const rewardTokenIcon = FarmUtils.tokenIconSrc(rewardToken?.name);
  const baseTokenDisplayName = baseToken?.displayName === 'ETHC' ? 'ETH' : baseToken?.displayName;
  const pairedTokenDisplayName = rewardToken?.displayName === 'ETHC' ? 'ETH' : rewardToken?.displayName;


  const totalLP = (
    <FormatNumber
      value={FarmUtils.amountInTokens(
        props.farmUserInfo?.totalLPUSD,
        baseAppSyncToken
      )}
      postfix={` ${baseTokenDisplayName}`}
      isLoading={false}
      displayDecimals={NumberUtils.determineDecimals(
        FarmUtils.amountInTokens(
          props.farmUserInfo?.totalLPUSD,
          baseAppSyncToken
        )
      )}
      useNumbro={true}
    />
  );

  const baseTokenAmount = (
    <FormatNumber
      value={FarmUtils.amountInTokens(
        baseToken?.totalAmountUSD,
        baseAppSyncToken
      )}
      postfix={` ${baseTokenDisplayName}`}
      isLoading={false}
      displayDecimals={NumberUtils.determineDecimals(
        FarmUtils.amountInTokens(baseToken?.totalAmountUSD, baseAppSyncToken)
      )}
      useNumbro={true}
    />
  );

  const rewardTokenAmount = (
    <FormatNumber
      value={FarmUtils.amountInTokens(
        rewardToken?.totalAmountUSD,
        memberAppSyncToken
      )}
      postfix={` ${pairedTokenDisplayName}`}
      isLoading={false}
      displayDecimals={NumberUtils.determineDecimals(
        FarmUtils.amountInTokens(
          rewardToken?.totalAmountUSD,
          memberAppSyncToken
        )
      )}
      useNumbro={true}
    />
  );

  return (
    <>
      <div className="flex flex-col max-w-xl mx-auto">
        <VaultTooltip
          depositTokenDisplayName={depositToken.displayName}
          chainId={chainId}
        />
        <div className="flex flex-col py-8 px-5 tablet:px-8 primary-text-color bordered rounded-[10px] shadow-md">
          <div className="flex flex-row justify-between">
            <div className="value-text heading-color font-semibold">
              Your Value
            </div>
            <SpinnerContainer isLoading={props.loadingUserInfo}>
              <div className="value-text font-semibold">{totalLP}</div>
            </SpinnerContainer>
          </div>

          {props.farm && (
            <animated.div style={fadeInProps} className="mb-5">
              <div className="relative">
                <div className="flex flex-col">
                  <div className="flex flex-row items-center justify-between mt-4">
                    <div className="flex flex-row items-center">
                      <Image
                        src={baseTokenIcon}
                        alt={`${baseToken?.name} icon`}
                        height="30px"
                        width="30px"
                        marginRight="5px"
                        marginLeft="5px"
                      />
                      <div className="value-text">{baseTokenDisplayName}</div>
                    </div>
                    <div className="flex flex-col">
                      <div className="value-text">{baseTokenAmount}</div>
                      {!isHodlVault && (
                        <div className="sub-text text-align-right">
                          {baseToken?.totalAmount}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-row items-center justify-between mt-2">
                    <div className="flex flex-row items-center">
                      <Image
                        src={rewardTokenIcon}
                        alt={`${memberAppSyncToken?.name} icon`}
                        height="30px"
                        width="30px"
                        marginRight="5px"
                        marginLeft="5px"
                      />
                      <div className="value-text">
                        {pairedTokenDisplayName}
                      </div>
                    </div>
                    <div className="flex-column">
                      <div className="value-text">{rewardTokenAmount}</div>
                      {!isHodlVault && (
                        <div className="sub-text text-align-right">
                          {rewardToken?.totalAmount}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </animated.div>
          )}
          <div className="flex justify-evenly items-center pt-8 box-footer border-t-2">
            <ExternalLinkButton
              to={props.farm?.extras.tradeUrl as string}
              disabled={!props.farm?.extras.tradeUrl}
              className="deposit-button icon-new-window-white h-16 tablet:h-10"
            >
              Trade
            </ExternalLinkButton>
            <button
              className="deposit-button h-16"
              type="button"
              onClick={props.onDepositOpen}
              data-dd-action-name="Add Liquidity Modal"
            >
              <span>Add Liquidity</span>
            </button>
            <button
              className="deposit-button"
              type="button"
              onClick={props.onWithdrawOpen}
              data-dd-action-name="Open Withdraw Modal"
            >
              <span>Withdraw</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Manage;
